import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Bookmark, ChevronLeft, Pause, Play } from 'lucide-react';
import { api, ApiError, fileUrl, type MediaItem, type MediaTab } from '../api/client.ts';
import { useChat } from '../chat/ChatContext.tsx';
import { Avatar } from '../components/Avatar.tsx';
import { ChatAvatarPicker } from '../components/ChatAvatarPicker.tsx';
import {
  ImageViewer,
  viewerForConversationMedia,
  type ViewerImage,
} from '../components/ImageViewer.tsx';
import { formatDuration, formatListTimestamp, presenceLabel } from '../lib/format.ts';
import './SettingsScreen.css';
import './PeerInfoScreen.css';

/** The three shelves, in the order the tab bar draws them. */
const TABS: { id: MediaTab; label: string }[] = [
  { id: 'media', label: 'Медиа' },
  { id: 'notes', label: 'Кружочки' },
  { id: 'voice', label: 'Голосовые' },
];

/**
 * A page's worth of tiles. Three columns, so a multiple of three fills whole
 * rows — a part-row at the bottom reads as the end of the shelf when it is only
 * the end of the page.
 */
const PAGE = 60;

/**
 * The other person, and everything the two of you have sent each other that is
 * not words: photographs and videos, кружочки, voice messages.
 *
 * «Заметки» land here too, and the shelves are the reason: a chat kept for
 * things worth keeping is the one where «покажи всё, что я сюда клал» is the
 * whole point of a profile. What changes is the head above them. A person's is
 * read — their name, their face, when they were last online — and this one is
 * written: the name and the picture on «Заметки» belong to the one person in
 * them, and there is no other screen for that to live on. Hence also what is
 * *not* swapped in: no presence line, because presence is a fact about somebody
 * else, and nobody else is here.
 *
 * Reached by tapping the conversation header, which is the only way in — hence
 * what is *not* here. There are no «написать / позвонить» buttons: this screen is
 * one tap from the conversation those actions live in, and a second place to do
 * the same thing is a second place to keep working. There is no phone number,
 * because the app has no notion of one. And there is no «⋮»: the design left its
 * contents undrawn, and a control that looks pressable and does nothing is worse
 * than no control — the same rule the conversation header follows.
 *
 * The grid pages rather than loading a conversation's whole history: `total` is
 * what the viewer's counter needs, and it arrives with the first page, so the
 * count is right long before the tiles behind it are.
 */
export function PeerInfoScreen() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { chats } = useChat();

  const [tab, setTab] = useState<MediaTab>('media');
  const [items, setItems] = useState<MediaItem[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  /**
   * Which shelf the rows in state belong to. A tab switched while its predecessor
   * was still in flight would otherwise append one shelf's tiles to another's.
   */
  const showing = useRef<MediaTab>('media');

  const chat = chats.find((one) => one.id === chatId) ?? null;

  // Nothing here describes a group — that is GroupInfoScreen's screen, and the
  // URL is shared. Somebody arriving at a group's /info is sent to the one that
  // does describe it rather than shown an empty profile.
  useEffect(() => {
    if (chat !== null && chat.kind === 'group') navigate(`/chat/${chatId}`, { replace: true });
  }, [chat, chatId, navigate]);

  const load = useCallback(
    (which: MediaTab, after: MediaItem | null) => {
      if (!chatId) return;
      showing.current = which;
      setLoading(true);
      api
        .conversationMedia(chatId, {
          tab: which,
          limit: PAGE,
          ...(after ? { before: after.messageId, beforeIndex: after.index } : {}),
        })
        .then((page) => {
          // The answer to a shelf nobody is looking at any more.
          if (showing.current !== which) return;
          setItems((previous) => (after ? [...previous, ...page.items] : page.items));
          setTotal(page.total);
          setHasMore(page.hasMore);
          setFailed(false);
        })
        .catch(() => {
          if (showing.current !== which) return;
          setFailed(true);
        })
        .finally(() => {
          if (showing.current === which) setLoading(false);
        });
    },
    [chatId],
  );

  useEffect(() => {
    setItems([]);
    setTotal(0);
    setHasMore(false);
    load(tab, null);
  }, [tab, load]);

  /**
   * The open viewer is a history entry rather than component state, for the same
   * reason it is one in the conversation: the Android back button then dismisses
   * the picture instead of leaving the screen. See ChatScreen.openImage.
   */
  const viewerImage = (location.state as { image?: ViewerImage } | null)?.image ?? null;

  function openAt(position: number) {
    navigate(location.pathname + location.search, {
      state: {
        image: viewerForConversationMedia(
          // Only what has been paged in — the viewer's counter still says how many
          // there are in total, and paging to the end of the loaded run is what
          // asks for the rest.
          items.map((item) => ({
            messageId: item.messageId,
            index: item.index,
            kind: item.kind === 'voice' ? 'video' : item.kind,
            fileName: item.fileName,
          })),
          position,
          total,
        ),
      },
    });
  }

  const peerName = chat?.peer?.displayName ?? chat?.title ?? '';
  const notes = chat?.kind === 'notes';

  return (
    <div className="app-frame settings-screen">
      {/* No heading text: the name below is several times the size and is the
          title. The back button is the whole of the bar, as the design draws it. */}
      <header className="peer-info-top">
        <button
          type="button"
          className="icon-button settings-back"
          onClick={() => navigate(`/chat/${chatId}`)}
          aria-label="Назад"
        >
          <ChevronLeft size={22} />
        </button>
      </header>

      <div className="peer-info-body">
        <div className="peer-info-head">
          {notes ? (
            <>
              {/* The bookmark the chat list draws on this row, several times
                  the size — the same tile, so the profile is recognisably the
                  thing that was tapped. */}
              <Avatar className="peer-info-avatar" photoId={chat.avatarPhotoId}>
                <Bookmark size={44} strokeWidth={1.4} />
              </Avatar>
              <h2 className="peer-info-name">{chat.title}</h2>
              {chatId && <ChatAvatarPicker chatId={chatId} onChanged={() => {}} />}
            </>
          ) : (
            <>
              <Avatar
                className="peer-info-avatar"
                photoId={chat?.peer?.avatarPhotoId ?? null}
                name={peerName}
              />
              <h2 className="peer-info-name">{peerName}</h2>
              <span className="peer-info-status">
                {chat === null
                  ? ''
                  : presenceLabel(chat.online, chat.lastSeenAt, chat.peer?.gender ?? null)}
              </span>
            </>
          )}
        </div>

        {notes && chatId && <NotesName chatId={chatId} title={chat.title} />}

        <div className="peer-info-tabs" role="tablist">
          {TABS.map((one) => (
            <button
              key={one.id}
              type="button"
              role="tab"
              aria-selected={tab === one.id}
              className={tab === one.id ? 'peer-info-tab peer-info-tab-on' : 'peer-info-tab'}
              onClick={() => setTab(one.id)}
            >
              {one.label}
            </button>
          ))}
        </div>

        {failed ? (
          <p className="peer-info-empty" role="alert">
            Не удалось загрузить. Проверьте соединение.
          </p>
        ) : items.length === 0 ? (
          <p className="peer-info-empty">
            {loading ? 'Загрузка…' : emptyLabel(tab)}
          </p>
        ) : tab === 'voice' ? (
          <VoiceList items={items} />
        ) : (
          <div className={tab === 'notes' ? 'peer-info-grid peer-info-grid-round' : 'peer-info-grid'}>
            {items.map((item, position) => (
              <MediaTile
                key={`${item.messageId}:${item.index ?? 'single'}`}
                item={item}
                onOpen={() => openAt(position)}
              />
            ))}
          </div>
        )}

        {hasMore && items.length > 0 && (
          <button
            type="button"
            className="peer-info-more"
            onClick={() => load(tab, items[items.length - 1] ?? null)}
            disabled={loading}
          >
            {loading ? 'Загрузка…' : 'Показать ещё'}
          </button>
        )}
      </div>

      {viewerImage && <ImageViewer image={viewerImage} onClose={() => navigate(-1)} />}
    </div>
  );
}

/**
 * The name on «Заметки», and the only writable thing on this screen.
 *
 * The field holds nothing of its own until somebody types: `draft` is null for
 * «what the chat is called», and the socket is what keeps that answer current —
 * renaming announces to every device the owner has, this one among them, so the
 * reply is not what updates the screen and a rename made on the phone lands
 * here without a reload. Clearing the draft after a save is what hands the
 * field back to that answer.
 *
 * An empty field saves rather than refusing, and what comes back is «Заметки»:
 * clearing a name one chose is how one un-chooses it, and an error there would
 * be the app insisting the chat be called something.
 */
function NotesName({ chatId, title }: { chatId: string; title: string }) {
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const value = draft ?? title;
  const changed = value.trim() !== title;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.renameChat(chatId, value.trim());
      setDraft(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить название');
    }
    setBusy(false);
  }

  return (
    <div className="peer-info-rename">
      <label className="field-label" htmlFor="notes-title">
        Название
      </label>
      <input
        id="notes-title"
        className="text-field"
        value={value}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Заметки"
        maxLength={60}
      />
      <button
        type="button"
        className="primary-button"
        onClick={() => void save()}
        disabled={busy || !changed}
      >
        Сохранить
      </button>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

function emptyLabel(tab: MediaTab): string {
  if (tab === 'notes') return 'Кружочков пока нет.';
  if (tab === 'voice') return 'Голосовых пока нет.';
  return 'Фото и видео пока нет.';
}

/**
 * One tile. A video says how long it runs; a video whose sender's browser never
 * grabbed a poster frame says nothing at all rather than pointing an `<img>` at
 * an MP4 — which is what `hasPreview` is for.
 */
function MediaTile({ item, onOpen }: { item: MediaItem; onOpen: () => void }) {
  return (
    <button type="button" className="peer-info-tile" onClick={onOpen}>
      {item.hasPreview ? (
        <img
          className="peer-info-tile-image"
          src={fileUrl(item.messageId, {
            variant: 'thumb',
            ...(item.index === null ? {} : { index: item.index }),
          })}
          alt=""
          loading="lazy"
        />
      ) : (
        <span className="peer-info-tile-blank" aria-hidden="true">
          <Play size={18} />
        </span>
      )}
      {item.kind === 'video' && (
        <span className="peer-info-tile-duration">
          <Play size={9} fill="currentColor" strokeWidth={0} />
          {item.durationMs === null ? '' : formatDuration(item.durationMs)}
        </span>
      )}
    </button>
  );
}

/**
 * The voice shelf is a list, not a grid: a recording has nothing to show, and a
 * square with a duration in it would be a tile pretending to be a picture.
 *
 * One `<audio>` for the whole list rather than one per row — a shelf of sixty
 * would otherwise be sixty elements, and only ever one of them can be playing.
 */
function VoiceList({ items }: { items: MediaItem[] }) {
  const [playing, setPlaying] = useState<string | null>(null);
  const sound = useRef<HTMLAudioElement | null>(null);

  // Leaving the screen mid-playback must not leave a voice talking to nobody.
  useEffect(() => () => sound.current?.pause(), []);

  function toggle(item: MediaItem) {
    const key = item.messageId;
    const element = sound.current;
    if (!element) return;
    if (playing === key) {
      element.pause();
      setPlaying(null);
      return;
    }
    element.src = fileUrl(item.messageId);
    void element.play().then(
      () => setPlaying(key),
      // Autoplay refused, or bytes that will not decode: the row goes back to
      // offering play rather than showing a pause that does nothing.
      () => setPlaying(null),
    );
  }

  return (
    <div className="peer-info-voices">
      <audio ref={sound} onEnded={() => setPlaying(null)} preload="none" />
      {items.map((item) => (
        <button
          key={item.messageId}
          type="button"
          className="peer-info-voice"
          onClick={() => toggle(item)}
        >
          <span className="peer-info-voice-play">
            {playing === item.messageId ? <Pause size={15} /> : <Play size={15} />}
          </span>
          <span className="peer-info-voice-body">
            <span className="peer-info-voice-length">
              {item.durationMs === null ? 'Голосовое' : formatDuration(item.durationMs)}
            </span>
            <span className="peer-info-voice-date">{formatListTimestamp(item.createdAt)}</span>
          </span>
        </button>
      ))}
    </div>
  );
}
